
/**
 * Band movement: one hourly slice of continuous walking toward the target.
 * Pure state arithmetic — the caller owns the grid and the events. Speeds
 * are per spec §12: an unburdened band walks ~2.5 km per world-day, a
 * migrating band drags its whole life along slower, a fleeing band drops
 * everything and runs.
 */

export const HOUR = 3600;

/** Walking speeds in km per world-day, by mode. */
export const SPEED_KMPD = { normal: 2.5, migrate: 1.8, flee: 3.2 } as const;
export type MoveMode = keyof typeof SPEED_KMPD;

/** Close enough to be there — the target clears inside this radius. */
export const ARRIVE_RADIUS = 40;

/**
 * Advance toward the target for `dtSeconds`. Mutates x/z, clears the target
 * on arrival, and reports metres actually walked — the caller turns that
 * into wear, cohesion cost, and (for migrations) a band_arrived event.
 */
/**
 * Anything that walks. A band used to be the only thing that did; now parties
 * do the walking and the band stands where its people are, so this asks for a
 * position and a destination rather than for a whole community.
 */
export interface Walker {
  x: number;
  z: number;
  target?: { x: number; z: number };
}

/**
 * Advance along a chain of waypoints for `dtSeconds`. The LAST waypoint is
 * the true destination and keeps `stepToward`'s exact arrival semantics —
 * clear the target inside ARRIVE_RADIUS, land exactly on it when the step
 * reaches it — while every earlier waypoint is passed THROUGH without
 * stopping: a bend in a worn way is a turn, not a destination. With a
 * single waypoint this is `stepToward` to the letter.
 */
export function stepAlong(
  band: Walker,
  waypoints: ReadonlyArray<{ x: number; z: number }>,
  mode: MoveMode,
  dtSeconds: number,
  /** ≥1: a worn path underfoot carries feet faster (see paths.ts). */
  speedMul = 1,
): { moved: number; arrived: boolean } {
  let budget = ((SPEED_KMPD[mode] * 1000 * speedMul) / 86400) * dtSeconds;
  let moved = 0;
  for (let w = 0; w < waypoints.length; w++) {
    const wp = waypoints[w];
    const final = w === waypoints.length - 1;
    const dx = wp.x - band.x;
    const dz = wp.z - band.z;
    const dist = Math.hypot(dx, dz);
    if (final) {
      if (dist <= ARRIVE_RADIUS) {
        delete band.target;
        return { moved, arrived: true };
      }
      if (dist - budget <= ARRIVE_RADIUS) {
        band.x = wp.x;
        band.z = wp.z;
        delete band.target;
        return { moved: moved + dist, arrived: true };
      }
    }
    if (!final && budget >= dist) {
      band.x = wp.x;
      band.z = wp.z;
      moved += dist;
      budget -= dist;
      continue;
    }
    if (budget <= 0 || dist === 0) break;
    band.x += (dx / dist) * budget;
    band.z += (dz / dist) * budget;
    moved += budget;
    break;
  }
  return { moved, arrived: false };
}

export function stepToward(
  band: Walker,
  mode: MoveMode,
  dtSeconds: number,
  /** ≥1: a worn path underfoot carries feet faster (see paths.ts). */
  speedMul = 1,
): { moved: number; arrived: boolean } {
  const tgt = band.target;
  if (!tgt) return { moved: 0, arrived: false };
  const dx = tgt.x - band.x;
  const dz = tgt.z - band.z;
  const dist = Math.hypot(dx, dz);
  if (dist <= ARRIVE_RADIUS) {
    delete band.target;
    return { moved: 0, arrived: true };
  }
  const step = ((SPEED_KMPD[mode] * 1000 * speedMul) / 86400) * dtSeconds;
  if (dist - step <= ARRIVE_RADIUS) {
    // land exactly on the target: arrival position must not depend on how
    // many hourly slices the walk happened to be cut into
    band.x = tgt.x;
    band.z = tgt.z;
    delete band.target;
    return { moved: dist, arrived: true };
  }
  band.x += (dx / dist) * step;
  band.z += (dz / dist) * step;
  return { moved: step, arrived: false };
}
